/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {PspRole} from '@shared/models/psp-role';
import addSeconds from 'date-fns/addSeconds';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';
import setMilliseconds from 'date-fns/setMilliseconds';

export interface PspToken {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  roles: Array<PspRole>;
  scopes: Array<string>;
  accountId?: string;
  userId?: string;
  // We manually set up the expiry date
  expiryDate: string;
}

/**
 * Checks if the current date is after the expiry date of the token
 * If it is, then the token should be counted as expired
 * @param token The PspToken that is checked
 * @returns boolean - Whether the token has expired
 */
export function isTokenExpired(token: PspToken): boolean {
  return isAfter(new Date(), parseISO(token.expiryDate));
}

/**
 * This interface describes how the PspTokenApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface PspTokenApiResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  roles: Array<PspRole>;
  scopes: Array<string>;
  account_id?: string;
  user_id?: string;
}

/**
 * This method transforms the PspTokenApiResponse into our own
 * PspToken object which our application understands
 * @param response the pagination API response
 * @returns the deserialized PspToken
 */
export const pspTokenFromApiResponse = (response: PspTokenApiResponse): PspToken => {
  return {
    accessToken: response.access_token,
    tokenType: response.token_type,
    expiresIn: response.expires_in,
    roles: response.roles,
    scopes: response.scopes,
    // Set the milliseconds to 0 as we don't need it to be that precise
    expiryDate: addSeconds(setMilliseconds(new Date(), 0), response.expires_in).toISOString(),
    ...(response.account_id && {accountId: response.account_id}),
    ...(response.user_id && {userId: response.user_id}),
  };
};
